import { Briefcase, Building2, Gauge, Target } from "lucide-react";
import Reveal from "@/components/ui/Reveal";
import {
  darkCard,
  darkCardBody,
  darkCardIcon,
  darkCardTitle,
} from "./darkCard";

/** The four ways one workforce differs, given a card each so the spread is seen at a glance. */
const differences = [
  {
    Icon: Briefcase,
    title: "Different roles",
    body: "Finance, sales, engineering and operations use AI in different ways.",
  },
  {
    Icon: Gauge,
    title: "Different skill levels",
    body: "Some people are already experimenting. Others are just getting started.",
  },
  {
    Icon: Target,
    title: "Different goals",
    body: "Each team needs AI to move a different business outcome.",
  },
  {
    Icon: Building2,
    title: "Different contexts",
    body: "Your industry, data and workflows are not a generic course's examples.",
  },
] as const;

/**
 * The first panel to slide up over the pinned hero (see HeroCoverTransition), so it is
 * fully opaque, in the hero's own colour (mkt-navbar). States the problem the rest of the page answers.
 */
export default function TheProblem() {
  return (
    <section
      id="problem"
      aria-labelledby="problem-heading"
      className="scroll-mt-20 relative isolate overflow-hidden bg-mkt-navbar py-size-xl px-margin-mobile md:px-margin-desktop"
    >
      {/* Blurred orb so the dark background reads as lit rather than flat, as in WhyNotChatGpt. */}
      <div
        aria-hidden="true"
        className="pointer-events-none absolute -top-40 left-[-10%] -z-10 h-[420px] w-[420px] rounded-full bg-mkt-primary/20 blur-[140px]"
      />

      <div className="max-w-[1440px] mx-auto">
        <Reveal className="max-w-4xl mb-size-lg">
          <div className="flex items-center gap-base mb-size-sm">
            <span className="w-12 h-[1px] bg-mkt-primary" />
            <span className="font-label-md text-label-md text-white uppercase tracking-widest">
              The problem
            </span>
          </div>
          <h2
            id="problem-heading"
            className="font-display-lg text-[clamp(32px,4vw,56px)] leading-[1.1] tracking-[-0.02em] font-bold text-white text-balance"
          >
            One-size-fits-all learning is a relic.
          </h2>
          <p className="mt-size-md font-headline-md text-[clamp(20px,2vw,28px)] leading-[1.3] font-semibold text-white">
            Your workforce isn&rsquo;t one-size-fits-all.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-gutter">
          {differences.map(({ Icon, title, body }, index) => (
            <Reveal
              key={title}
              delayMs={[0, 100, 200, 300][index]}
              className="h-full"
            >
              <div
                className={`${darkCard} h-full p-size-md sm:p-size-lg flex flex-col gap-size-md`}
              >
                <span
                  className={`flex h-12 w-12 items-center justify-center rounded-full ${darkCardIcon}`}
                >
                  <Icon
                    aria-hidden="true"
                    strokeWidth={1.5}
                    className="h-6 w-6"
                  />
                </span>
                <h3
                  className={`font-headline-lg text-headline-sm font-semibold ${darkCardTitle}`}
                >
                  {title}.
                </h3>
                <p className={`font-body-md text-body-md ${darkCardBody}`}>
                  {body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
